import {
    Component,
    ElementRef,
    Injector,
    PLATFORM_ID,
    StateKey,
    TransferState,
    afterNextRender,
    computed,
    effect,
    inject,
    input,
    linkedSignal,
    makeStateKey,
    signal,
    viewChild,
} from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { ButtonComponent, ContainerComponent, IDynamicElement, LinkDirective } from '@sirius/common';
import { TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';

import { AlternativesPanelComponent } from './components/alternatives-panel/alternatives-panel.component';
import { RequirementSliderComponent } from './components/requirement-slider/requirement-slider.component';
import { COLLAPSED_RESULT_COUNT, ResultListComponent } from './components/result-list/result-list.component';
import { RouteNoticeComponent } from './components/route-notice/route-notice.component';
import { TaxonomySelectComponent } from './components/taxonomy-select/taxonomy-select.component';
import { closestAlternatives } from './domain/alternatives';
import { isEligible, ratedPayloadKg } from './domain/eligibility';
import { featured, rank, toRanked } from './domain/ranking';
import { findRecommendation, recommend, recommendationOptions } from './domain/recommendations';
import { IStatusTemplates, fillNoMatchSubtext, fillRankedSubheading, fillTemplate } from './domain/status-template';
import { IRequirement, IRobotOption, IRobotSelector } from './models/robot-selector.model';

const STRINGS_STATE_KEY: StateKey<Record<string, string>> =
    makeStateKey<Record<string, string>>('robotSelector.strings');

export const I18N_KEYS = [
    'robotSelector.guidedLegend',
    'robotSelector.specsLegend',
    'robotSelector.payloadLabel',
    'robotSelector.reachLabel',
    'robotSelector.reset',
    'robotSelector.undo',
    'robotSelector.bestMatch',
    'robotSelector.showAll',
    'robotSelector.showFewer',
    'robotSelector.viewProduct',
    'robotSelector.resultsAnnouncement',
    'robotSelector.guidedSubheading',
    'robotSelector.industryLabel',
    'robotSelector.applicationLabel',
    'robotSelector.optionalSuffix',
    'robotSelector.noSelection',
    'robotSelector.guidedResetBySpecs',
    'robotSelector.specsResetByGuided',
    'robotSelector.selectionClearedIndustry',
    'robotSelector.selectionClearedApplication',
    'robotSelector.relaxTo',
    'robotSelector.noMatchAnnouncement',
] as const;

type I18nKey = (typeof I18N_KEYS)[number];

/**
 * The two ways in. `guided`: an industry and/or application is chosen, and the editors' pick list is shown.
 * `specs`: the visitor has set payload and reach, and every robot that meets them is ranked by fit.
 * `featured`: neither yet, and the portfolio is shown with the g-Series leading.
 */
export type SelectorRoute = 'featured' | 'guided' | 'specs';

type FilterGroup = 'guided' | 'specs';

type TaxonomyDimension = 'industry' | 'application';

interface IRouteSnapshot {
    requirement: IRequirement;
    slidersTouched: boolean;
}

interface IRouteNotice {
    message: string;
    /** The group whose values were just reset; Undo restores it and moves focus back to it. */
    resetGroup: FilterGroup;
    undo: IRouteSnapshot;
}

const FALLBACK_LOCALE = 'en-US';

@Component({
    selector: 'robot-selector-root',
    templateUrl: './robot-selector.component.html',
    styleUrl: './robot-selector.component.scss',
    imports: [
        AlternativesPanelComponent,
        ButtonComponent,
        ContainerComponent,
        LinkDirective,
        RequirementSliderComponent,
        ResultListComponent,
        RouteNoticeComponent,
        TaxonomySelectComponent,
    ],
    host: {
        'class': 'robot-selector',
    },
})
export class RobotSelectorComponent implements IDynamicElement<IRobotSelector> {
    readonly config = input<IRobotSelector>();

    private readonly translate = inject(TranslateService);
    private readonly injector = inject(Injector);

    private readonly transferState = inject(TransferState);
    private readonly isServer = isPlatformServer(inject(PLATFORM_ID));

    // Preserve translated chrome across the non-hydrated SSR handoff.
    private readonly strings = toSignal(this.translate.stream([...I18N_KEYS]), {
        initialValue: this.transferState.get(STRINGS_STATE_KEY, {} as Record<string, string>),
    });

    readonly locale = toSignal(this.translate.onLangChange.pipe(map((event) => event.lang)), {
        initialValue: this.translate.getCurrentLang() || FALLBACK_LOCALE,
    });

    private readonly guidedGroup = viewChild<ElementRef<HTMLElement>>('guidedGroup');
    private readonly specsGroup = viewChild<ElementRef<HTMLElement>>('specsGroup');

    private readonly robots = computed(() => this.config()?.robots ?? []);
    private readonly recommendations = computed(() => this.config()?.recommendations ?? []);

    readonly hasDataset = computed(() => {
        const config = this.config();
        return !!config && config.robots.length > 0 && !!config.ranges;
    });

    readonly ranges = computed(() => this.config()?.ranges ?? null);

    readonly requirement = linkedSignal<IRequirement>(() => this.initialRequirement());

    readonly slidersTouched = signal(false);

    readonly notice = signal<IRouteNotice | null>(null);

    readonly isExpanded = signal(false);

    readonly route = computed<SelectorRoute>(() => {
        const { industry, application } = this.requirement();

        if (industry !== null || application !== null) {
            return 'guided';
        }

        return this.slidersTouched() ? 'specs' : 'featured';
    });

    readonly results = computed(() => {
        const requirement = this.requirement();
        const robots = this.robots();

        switch (this.route()) {
            case 'guided':
                return toRanked(recommend(robots, this.recommendations(), requirement));
            case 'specs':
                return rank(
                    robots.filter((robot) => isEligible(robot, requirement)),
                    requirement
                );
            default:
                return featured(robots);
        }
    });

    readonly taxonomyOptions = computed(() =>
        recommendationOptions(this.recommendations(), this.config()?.taxonomyLabels ?? null, this.requirement())
    );

    /** Hidden until editors have authored pick lists; the specs route then stands on its own. */
    readonly hasGuidedRoute = computed(
        () => this.taxonomyOptions().industries.length > 0 || this.taxonomyOptions().applications.length > 0
    );

    readonly isNoMatch = computed(() => this.hasDataset() && this.route() === 'specs' && this.results().length === 0);

    readonly alternatives = computed(() =>
        this.isNoMatch() ? closestAlternatives(this.robots(), this.requirement()) : []
    );

    readonly heading = computed(() => this.configuredText('heading'));
    readonly subheading = computed(() => this.configuredText('subheading'));
    readonly disclaimer = computed(() => this.configuredText('disclaimer'));

    readonly resultsHeading = computed(() => {
        if (this.isNoMatch()) {
            return this.configuredText('noMatchHeading');
        }

        return this.route() === 'featured'
            ? this.configuredText('resultsHeading')
            : this.configuredText('resultsHeadingRanked');
    });

    readonly expertRoute = computed(() => {
        const link = this.config()?.expertLink ?? null;
        const text = this.configuredText('expertLinkText');

        return link && text ? { link, text } : null;
    });

    readonly statusTemplates = computed<IStatusTemplates>(() => ({
        payloadMet: this.config()?.text?.alternativeStatusPayloadMet ?? null,
        reachMet: this.config()?.text?.alternativeStatusReachMet ?? null,
        bothShort: this.config()?.text?.alternativeStatusBothShort ?? null,
        locale: this.locale(),
    }));

    readonly resultsSubheading = computed(() => {
        const portfolioSize = this.robots().length;

        if (this.isNoMatch()) {
            return fillNoMatchSubtext(this.configuredText('noMatchSubtext'), this.requirement(), this.statusTemplates());
        }

        switch (this.route()) {
            case 'featured':
                return fillTemplate(this.configuredText('resultsSubheading'), { count: String(portfolioSize) });
            case 'guided':
                return fillTemplate(this.translation('robotSelector.guidedSubheading'), {
                    selection: this.selectionLabel(),
                });
            default:
                return fillRankedSubheading(
                    this.configuredText('resultsSubheadingRanked'),
                    { matches: this.results().length, portfolioSize },
                    this.requirement(),
                    this.statusTemplates()
                );
        }
    });

    readonly announcement = computed(() => {
        if (this.route() === 'featured') {
            return '';
        }

        return this.isNoMatch()
            ? this.translation('robotSelector.noMatchAnnouncement')
            : fillTemplate(this.translation('robotSelector.resultsAnnouncement'), {
                  count: String(this.results().length),
              });
    });

    readonly canExpand = computed(() => this.results().length > COLLAPSED_RESULT_COUNT);

    readonly toggleLabel = computed(() =>
        this.isExpanded()
            ? fillTemplate(this.translation('robotSelector.showFewer'), { count: String(COLLAPSED_RESULT_COUNT) })
            : this.translation('robotSelector.showAll')
    );

    readonly guidedLegend = computed(() => this.translation('robotSelector.guidedLegend'));
    readonly specsLegend = computed(() => this.translation('robotSelector.specsLegend'));
    readonly payloadLabel = computed(() => this.translation('robotSelector.payloadLabel'));
    readonly reachLabel = computed(() => this.translation('robotSelector.reachLabel'));
    readonly industryLabel = computed(() => this.translation('robotSelector.industryLabel'));
    readonly applicationLabel = computed(() => this.translation('robotSelector.applicationLabel'));
    readonly optionalSuffix = computed(() => this.translation('robotSelector.optionalSuffix'));
    readonly noSelectionLabel = computed(() => this.translation('robotSelector.noSelection'));
    readonly resetLabel = computed(() => this.translation('robotSelector.reset'));
    readonly undoLabel = computed(() => this.translation('robotSelector.undo'));
    readonly bestMatchLabel = computed(() => this.translation('robotSelector.bestMatch'));
    readonly viewProductLabel = computed(() => this.translation('robotSelector.viewProduct'));
    readonly relaxRequirementLabel = computed(() => this.translation('robotSelector.relaxTo'));

    constructor() {
        if (this.isServer) {
            effect(() => {
                const resolved = this.strings();

                if (Object.keys(resolved).length > 0) {
                    this.transferState.set(STRINGS_STATE_KEY, resolved);
                }
            });
        }
    }

    onPayloadSettled(payloadKg: number): void {
        this.applySpecs({ payloadKg });
    }

    onReachSettled(reachMm: number): void {
        this.applySpecs({ reachMm });
    }

    onIndustryChange(industry: string | null): void {
        this.applyGuided({ industry }, 'industry');
    }

    onApplicationChange(application: string | null): void {
        this.applyGuided({ application }, 'application');
    }

    onAlternativeSelected(robot: IRobotOption): void {
        this.commit(
            { payloadKg: ratedPayloadKg(robot), reachMm: robot.reachMm, industry: null, application: null },
            true,
            null
        );
    }

    toggleExpanded(): void {
        this.isExpanded.set(!this.isExpanded());
    }

    reset(): void {
        this.commit(this.initialRequirement(), false, null);
    }

    undo(): void {
        const notice = this.notice();
        if (!notice) {
            return;
        }

        this.commit(notice.undo.requirement, notice.undo.slidersTouched, null);

        const group = notice.resetGroup === 'guided' ? this.guidedGroup() : this.specsGroup();
        afterNextRender(() => group?.nativeElement.querySelector<HTMLElement>('select, input')?.focus(), {
            injector: this.injector,
        });
    }

    /** Moving a slider leaves the guided route: the industry and application go back to "Any", visibly. */
    private applySpecs(change: Partial<Pick<IRequirement, 'payloadKg' | 'reachMm'>>): void {
        const before = this.snapshot();
        const leftGuided = this.route() === 'guided';

        this.commit(
            { ...before.requirement, ...change, industry: null, application: null },
            true,
            leftGuided ? this.routeNotice('robotSelector.guidedResetBySpecs', 'guided', before) : null
        );
    }

    /** Picking an industry or application leaves the specs route: payload and reach go back to their defaults. */
    private applyGuided(change: Partial<Pick<IRequirement, 'industry' | 'application'>>, changed: TaxonomyDimension): void {
        const before = this.snapshot();
        let next: IRequirement = { ...before.requirement, ...change };
        let notice: IRouteNotice | null = null;

        if (this.route() === 'specs') {
            const { payloadKg, reachMm } = this.initialRequirement();
            next = { ...next, payloadKg, reachMm };
            notice = this.routeNotice('robotSelector.specsResetByGuided', 'specs', before);
        }

        // Only combinations with a pick list are offered, but a pair can still outlive a cleared partner.
        const hasSelection = next.industry !== null || next.application !== null;
        if (hasSelection && !findRecommendation(this.recommendations(), next)) {
            const other: TaxonomyDimension = changed === 'industry' ? 'application' : 'industry';
            next = { ...next, [other]: null };
            notice = this.routeNotice(
                other === 'industry' ? 'robotSelector.selectionClearedIndustry' : 'robotSelector.selectionClearedApplication',
                'guided',
                before
            );

            if (!findRecommendation(this.recommendations(), next)) {
                next = { ...next, industry: null, application: null };
            }
        }

        this.commit(next, false, notice);
    }

    private commit(requirement: IRequirement, slidersTouched: boolean, notice: IRouteNotice | null): void {
        this.requirement.set(requirement);
        this.slidersTouched.set(slidersTouched);
        this.notice.set(notice);
        this.isExpanded.set(false);
    }

    private snapshot(): IRouteSnapshot {
        return { requirement: this.requirement(), slidersTouched: this.slidersTouched() };
    }

    private routeNotice(key: I18nKey, resetGroup: FilterGroup, undo: IRouteSnapshot): IRouteNotice {
        return { message: this.translation(key), resetGroup, undo };
    }

    private selectionLabel(): string {
        const { industry, application } = this.requirement();
        const labels = this.config()?.taxonomyLabels;

        return [industry && labels?.industries[industry], application && labels?.applications[application]]
            .filter((label): label is string => !!label)
            .join(' · ');
    }

    private translation(key: I18nKey): string {
        const value = this.strings()[key];
        return value && value !== key ? value : '';
    }

    private configuredText(
        field:
            | 'heading'
            | 'resultsHeading'
            | 'resultsSubheading'
            | 'resultsHeadingRanked'
            | 'resultsSubheadingRanked'
            | 'subheading'
            | 'disclaimer'
            | 'expertLinkText'
            | 'noMatchHeading'
            | 'noMatchSubtext'
    ): string {
        return this.config()?.text?.[field]?.trim() ?? '';
    }

    private initialRequirement(): IRequirement {
        const defaults = this.config()?.defaults;

        return {
            payloadKg: defaults?.payloadKg ?? 0,
            reachMm: defaults?.reachMm ?? 0,
            industry: null,
            application: null,
        };
    }
}
